import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  UntypedFormArray,
  Validators,
} from '@angular/forms';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { ReferenceDao } from '@app/dao/reference/reference.dao';
import { combineLatest, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { MatSelect } from '@angular/material/select';
import { AttributionDao } from '@app/dao/attribution/attribution.dao';
import { SwisslifeLocationService } from '@app/services/swisslife-location.service';
import { SubscriptionFacadeService } from '../../services/subscription-facade.service';
import { EaSubscriptionBuilder } from './ea-subscription-form.builder';

@Component({
  selector: 'birth-info-form',
  templateUrl: './birth-info-form.component.html',
  styleUrls: ['./birth-info-form.component.scss'],
  standalone: false,
})
export class BirthInfoFormComponent
  extends EaSubscriptionBuilder
  implements OnInit, OnDestroy, AfterViewInit
{
  @ViewChild('select', { static: false }) select: MatSelect;
  subscriptions: any[] = [];
  countries: any[];
  cities: any[];
  departments: any[];
  doneSubject: Subject<void> = new Subject<void>();
  page: number = 1;
  sessionConsultant: boolean;
  cristalisOrganizationId: number = 251251251;
  isCristalisOrganization: boolean;
  destroy$ = new Subject<void>();
  commands = null;
  isSwisslifeProduct: boolean = false;

  constructor(
    protected formConstructor: PrFormConstructor,
    private referenceDao: ReferenceDao,

    private cd: ChangeDetectorRef,
    private subscriptionFacade: SubscriptionFacadeService,
    private attributionDao: AttributionDao,
    private swisslifeLocation: SwisslifeLocationService,
  ) {
    super(formConstructor);
    // new add
  }
  ngAfterViewInit(): void {
    this.cd.detectChanges();
  }

  hasFinancileProduct(pledge: any): boolean {
    return !!pledge?.products
      ?.filter((product: any) => !!product?.parameters?.partnerFlux)
      ?.some(
        (product: any) => product?.parameters?.partnerFlux === 'FINANCILE',
      );
  }

  ngOnInit() {
    this.attributionDao.getOrganization().subscribe((data: any) => {
      this.isCristalisOrganization = data.id === this.cristalisOrganizationId;
    });
    const pledge = this.subscriptionFacade.getPledge();
    this.isSwisslifeProduct =
      this.swisslifeLocation.hasSwisslifeProduct(pledge);

    this.sessionConsultant =
      !!pledge && !!this.subscriptionFacade?.getUser()?.sessionConsultant;

    this.subscriptionFacade.initForm(this, {
      sessionConsultant: this.sessionConsultant,
      isCristalisOrganization: this.isCristalisOrganization,
      isFinancileProduct: this.hasFinancileProduct(pledge),
    });

    combineLatest([
      this.referenceDao.getCountries(),
      this.referenceDao.getDepartment(),
      this.referenceDao.getCities(),
    ]).subscribe(([countries, departments, cities]) => {
      this.countries = countries;
      this.cities = cities;
      this.departments = [
        ...departments,
        {
          id: '99',
          value: "Né à l'étranger",
        },
      ];
    });

    // The form is built asynchronously inside `subscriptionFacade.initForm` —
    // when the pledge BehaviorSubject emits, prForm.initForm rebuilds `this.form`.
    // We can only attach valueChanges listeners after that rebuild, so we run
    // setup every time pledgeChanged$ emits a non-null pledge.
    this.subscriptionFacade.pledgeChanged$
      .pipe(
        filter(p => !!p && this.owners?.controls?.length > 0),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.setupBirthFieldsListeners());
  }

  private birthFieldsListenersSetup$ = new Subject<void>();

  private setupBirthFieldsListeners() {
    const FOREIGN_DEPARTMENT_ID = '99';
    const strictZipValidators = [
      Validators.required,
      Validators.minLength(5),
      Validators.maxLength(5),
      Validators.pattern('[0-9]+'),
    ];

    // Tear down listeners attached to a previous form instance before
    // re-attaching to the freshly rebuilt form.
    this.birthFieldsListenersSetup$.next();

    this.owners.controls.forEach(owner => {
      const countryCtrl = owner.get('userBirthCountry');
      const zipCtrl = owner.get('userBirthZipCode');
      const cityCtrl = owner.get('userBirthCity');
      const locationCtrl = owner.get('userBirthLocation');

      if (!countryCtrl || !zipCtrl || !cityCtrl || !locationCtrl) return;

      const isForeignDepartment = (value: any) =>
        value != null && String(value) === FOREIGN_DEPARTMENT_ID;

      const reconcileZipValidators = () => {
        if (
          countryCtrl.value === 'FR' &&
          !isForeignDepartment(locationCtrl.value)
        ) {
          zipCtrl.setValidators(strictZipValidators);
        } else {
          zipCtrl.clearValidators();
        }
        zipCtrl.updateValueAndValidity({ emitEvent: false });
      };

      const teardown$ = this.birthFieldsListenersSetup$.pipe(
        takeUntil(this.destroy$),
      );

      countryCtrl.valueChanges.pipe(takeUntil(teardown$)).subscribe(value => {
        if (this.isSwisslifeProduct) {
          zipCtrl.setValue('', { emitEvent: false });
          cityCtrl.setValue('', { emitEvent: false });
        }

        if (value !== 'FR') {
          if (!isForeignDepartment(locationCtrl.value)) {
            locationCtrl.setValue(FOREIGN_DEPARTMENT_ID, { emitEvent: false });
          }
        } else if (isForeignDepartment(locationCtrl.value)) {
          locationCtrl.setValue(null, { emitEvent: false });
        }

        reconcileZipValidators();
      });

      locationCtrl.valueChanges
        .pipe(takeUntil(teardown$))
        .subscribe(() => reconcileZipValidators());

      zipCtrl.valueChanges.pipe(takeUntil(teardown$)).subscribe(() => {
        if (this.isSwisslifeProduct) {
          cityCtrl.setValue('', { emitEvent: false });
        }
      });

      reconcileZipValidators();
    });
  }

  isOwnerBirthCountryNotFR(owner: FormGroup): boolean {
    return owner.get('userBirthCountry').value !== 'FR';
  }

  filteredCities(owner: FormGroup) {
    return this.swisslifeLocation.filterCitiesByZipCode(
      this.cities,
      owner.get('userBirthZipCode')?.value,
    );
  }

  /**
   * Called when the CGP picks a city from the "Je ne trouve pas ma ville / mon
   * code postal" finder modal. The finder returns the official INSEE/La Poste
   * pair, so we patch both the zip code and the city at once.
   */
  onBirthCitySelected(
    owner: AbstractControl,
    selection: { city: string; zipCode: string },
  ) {
    if (!selection) return;
    const zipCtrl = owner.get('userBirthZipCode');
    const cityCtrl = owner.get('userBirthCity');
    if (!zipCtrl || !cityCtrl) return;
    // Use emitEvent: false everywhere — otherwise updating the zip code would
    // trigger the Swisslife valueChanges listener that clears the city, wiping
    // the value we are about to set.
    zipCtrl.setValue(selection.zipCode, { emitEvent: false });
    zipCtrl.updateValueAndValidity({ emitEvent: false });
    cityCtrl.setValue(selection.city, { emitEvent: false });
    cityCtrl.updateValueAndValidity({ emitEvent: false });
  }

  get owners() {
    return this.form.get('owners') as UntypedFormArray;
  }
  onSubmit() {
    this.form.markAsDirty();
    this.subscriptionFacade.submitPledge(this);
  }

  prev() {
    this.subscriptionFacade.previousStep();
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
